import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, FileText, Sparkles, Trash2, Download, ListChecks, Plus, Check, AlertTriangle, Pill, Accessibility, MessageSquare, Zap, Phone, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";

const KP_SECTIONS = [
  { key: "allergies", label: "Allergies", icon: AlertTriangle, color: "#C53030" },
  { key: "medications", label: "Medications", icon: Pill, color: "#2B6CB0" },
  { key: "mobility", label: "Mobility", icon: Accessibility, color: "#2F855A" },
  { key: "communication", label: "Communication", icon: MessageSquare, color: "#6B46C1" },
  { key: "triggers", label: "Triggers & distress", icon: Zap, color: "#C05621" },
  { key: "emergency_contacts", label: "Emergency contacts", icon: Phone, color: "#B83280" },
  { key: "other", label: "Other notes", icon: Info, color: "#4A5568" },
];

export default function CarePlansPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { canUsePro } = useAuth();
  const [plans, setPlans] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [suggestions, setSuggestions] = useState(null); // {planId, routines}
  const [added, setAdded] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/profiles/${pid}/care-plans`);
      setPlans(data);
    } catch (e) {
      toast.error(apiError(e));
      setPlans([]);
    }
  }, [pid]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles");
        setProfileName(data.find((p) => p.id === pid)?.name || "");
      } catch { /* optional */ }
      await load();
    })();
  }, [load, pid]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/profiles/${pid}/care-plans`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Care plan uploaded");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const summarize = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/care-plans/${id}/summarize`);
      toast.success("Summary ready");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const findRoutines = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/care-plans/${id}/routines`);
      setSuggestions({ planId: id, routines: data.routines });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const addRoutine = async (r, idx) => {
    try {
      await api.post("/routines", { profile_id: pid, name: r.name, icon: r.icon, steps: r.steps.map((s) => ({ label: s.label, icon: s.icon })) });
      setAdded((a) => ({ ...a, [idx]: true }));
      toast.success(`"${r.name}" added to routines`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const download = async (id, name) => {
    try {
      const res = await api.get(`/care-plans/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/care-plans/${id}`);
      toast.success("Care plan deleted");
      setPlans((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="care-plans-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-6 pb-28 space-y-6">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight">Care plans{profileName ? ` — ${profileName}` : ""}</h1>
          {canUsePro ? (
            <>
              <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png" className="hidden" data-testid="care-plan-file-input" onChange={(e) => upload(e.target.files?.[0])} />
              <button
                data-testid="upload-care-plan-button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-50"
              >
                <Upload size={20} strokeWidth={2.5} /> {busy ? "Uploading…" : "Upload care plan"}
              </button>
            </>
          ) : (
            <button data-testid="care-plans-upgrade-button" onClick={() => navigate("/premium")} className="min-h-[56px] px-6 rounded-full bg-[#ECC94B] font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5">
              <Lock size={20} strokeWidth={2.5} /> Pro feature — upgrade
            </button>
          )}
        </div>
        <p className="text-muted-foreground font-semibold">Upload a PDF or a photo scan. AI summarizes it, highlights allergies, medications, triggers and emergency contacts, and suggests routines.</p>

        {plans === null ? (
          <p className="text-lg text-muted-foreground">Loading…</p>
        ) : plans.length === 0 ? (
          <p data-testid="no-care-plans" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
            No care plans yet. Upload one to get an AI summary and routine suggestions.
          </p>
        ) : (
          <>
          {Date.now() - new Date(plans[0].created_at).getTime() > 180 * 24 * 60 * 60 * 1000 && (
            <div data-testid="plan-review-reminder" className="bg-[#FEEBC8] border-2 border-[#ED8936] rounded-3xl p-5 flex items-center gap-3">
              <AlertTriangle size={28} strokeWidth={2.5} className="text-[#C05621] shrink-0" />
              <p className="font-bold text-[#7B341E]">This care plan is over 6 months old — time for a review? Upload the latest version to keep summaries and routines accurate.</p>
            </div>
          )}
          {plans.map((p) => (
            <section key={p.id} data-testid={`care-plan-${p.id}`} className="bg-white rounded-3xl border-2 border-foreground/15 p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <FileText size={26} strokeWidth={2.5} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="font-extrabold text-lg truncate">{p.original_filename}</p>
                  <p className="text-sm font-semibold text-muted-foreground">Uploaded by {p.author_name} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <div className="ml-auto flex gap-2">
                  <button data-testid={`download-plan-${p.id}`} onClick={() => download(p.id, p.original_filename)} aria-label="Download" className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                    <Download size={20} strokeWidth={2.5} />
                  </button>
                  <button data-testid={`delete-plan-${p.id}`} onClick={() => remove(p.id)} aria-label="Delete" className="min-w-[48px] min-h-[48px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                    <Trash2 size={20} strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {!p.summary ? (
                <button
                  data-testid={`summarize-plan-${p.id}`}
                  onClick={() => summarize(p.id)}
                  disabled={busyId === p.id}
                  className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <Sparkles size={20} strokeWidth={2.5} /> {busyId === p.id ? "Reading document…" : "Summarize with AI"}
                </button>
              ) : (
                <>
                  <div data-testid={`summary-${p.id}`} className="bg-secondary/50 rounded-2xl p-5">
                    <p className="font-bold mb-1">Summary</p>
                    <p className="text-base leading-relaxed">{p.summary}</p>
                  </div>
                  {p.key_points && (
                    <div data-testid={`key-points-${p.id}`} className="grid sm:grid-cols-2 gap-3">
                      {KP_SECTIONS.filter((s) => (p.key_points[s.key] || []).length > 0).map((s) => {
                        const Icon = s.icon;
                        return (
                          <div key={s.key} data-testid={`kp-${s.key}-${p.id}`} className="rounded-2xl border-2 p-4" style={{ borderColor: `${s.color}55` }}>
                            <p className="font-extrabold flex items-center gap-2 mb-2" style={{ color: s.color }}>
                              <Icon size={20} strokeWidth={2.5} /> {s.label}
                            </p>
                            <ul className="space-y-1">
                              {p.key_points[s.key].map((item, i) => (
                                <li key={i} className="text-sm font-semibold flex gap-2"><span className="text-muted-foreground">•</span>{item}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button
                    data-testid={`find-routines-${p.id}`}
                    onClick={() => findRoutines(p.id)}
                    disabled={busyId === p.id}
                    className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <ListChecks size={20} strokeWidth={2.5} /> {busyId === p.id ? "Finding routines…" : "Find routines in this plan"}
                  </button>
                </>
              )}

              {suggestions?.planId === p.id && (
                <div data-testid={`routine-suggestions-${p.id}`} className="space-y-3">
                  <p className="font-extrabold text-lg">Suggested routines — tap to add:</p>
                  {suggestions.routines.map((r, idx) => (
                    <div key={idx} className="rounded-2xl border-2 border-foreground/15 p-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div>
                          <p className="font-extrabold">{r.name}</p>
                          <p className="text-sm font-semibold text-muted-foreground">{r.reason}</p>
                        </div>
                        <button
                          data-testid={`add-suggestion-${p.id}-${idx}`}
                          onClick={() => addRoutine(r, idx)}
                          disabled={added[idx]}
                          className={`ml-auto min-h-[48px] px-5 rounded-full font-bold flex items-center gap-2 transition-colors duration-200 ${added[idx] ? "bg-[#C6F6D5] text-[#22543D]" : "bg-primary text-white hover:-translate-y-0.5 transition-transform duration-200 ease-out ease-out"}`}
                        >
                          {added[idx] ? <><Check size={18} strokeWidth={2.5} /> Added</> : <><Plus size={18} strokeWidth={2.5} /> Add</>}
                        </button>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-muted-foreground">{r.steps.map((s) => s.label).join(" → ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
          </>
        )}
      </main>
    </div>
  );
}
